import { Fragment, useState, useCallback } from "react"
import useWorldStore from "../store/worldStore"
import useFloatingWindowLayer from "../hooks/useFloatingWindowLayer"
import { getTaskIdentity } from "../lib/taskIdentity"
import {
  normalizeTaskArtifacts,
  normalizeTaskResultText,
} from "../lib/taskArtifacts.mjs"
import {
  shouldSkipPointerDragStart,
  startPointerSession,
} from "../../../lib/pointer-drag"

const mono = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" }

function formatErrorValue(value) {
  if (value === null || value === undefined) return ""
  if (typeof value === "string") return value.trim()
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function getTaskErrorMeta(task) {
  const details =
    task?.error_details && typeof task.error_details === "object"
      ? task.error_details
      : null
  if (!details) return null

  const rows = [
    details.source ? ["Source", String(details.source)] : null,
    details.phase ? ["Phase", String(details.phase)] : null,
    details.method ? ["Method", String(details.method)] : null,
    Number.isFinite(Number(details.status))
      ? ["Status", String(details.status)]
      : null,
    details.lease_id ? ["Lease", String(details.lease_id)] : null,
    details.previous_lease_id
      ? ["Previous Lease", String(details.previous_lease_id)]
      : null,
    typeof details.retry_attempted === "boolean"
      ? ["Retried", details.retry_attempted ? "yes" : "no"]
      : null,
    details.error_type ? ["Type", String(details.error_type)] : null,
  ].filter(Boolean)
  const rawDetail = formatErrorValue(details.raw_detail)
  const rawResponse = formatErrorValue(details.raw_response)
  if (rows.length === 0 && !rawDetail && !rawResponse) {
    return null
  }

  return {
    rows,
    rawDetail,
    rawResponse,
  }
}

function timeAgo(ts) {
  if (!ts) return ""
  const s = Math.floor((Date.now() - ts) / 1000)
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  return `${Math.floor(s / 3600)}h ago`
}

export default function TaskResultModal({ task, onClose }) {
  const agents = useWorldStore((s) => s.agents)
  const openChat = useWorldStore((s) => s.openChat)
  const addMessage = useWorldStore((s) => s.addMessage)

  const agent = agents.find((a) => a.id === task.agentId)
  const color = agent?.color || "#888"
  const isComplete = task.status === "complete"
  const isCancelled = task.status === "cancelled"
  const statusLabel = isComplete
    ? "✓ Complete"
    : isCancelled
      ? "• Cancelled"
      : "✗ Failed"
  const statusColor = isComplete
    ? "#4ade80"
    : isCancelled
      ? "rgba(255,255,255,0.45)"
      : "#ef4444"
  const summary = typeof task.summary === "string" ? task.summary.trim() : ""
  const windowId = `task-result:${getTaskIdentity(task)}`
  const { zIndex, focusProps, focusWindow } = useFloatingWindowLayer(windowId)
  const artifacts = normalizeTaskArtifacts(task.artifacts)
  const resultText = normalizeTaskResultText(task.result, artifacts)
  const errorMeta = getTaskErrorMeta(task)

  const [pos, setPos] = useState({
    x: Math.round((window.innerWidth - 500) / 2),
    y: Math.round((window.innerHeight - 400) / 2),
  })
  const [size, setSize] = useState({ w: 500, h: 400 })
  const [stepsOpen, setStepsOpen] = useState(false)

  // Drag
  const onDragStart = useCallback(
    (e) => {
      if (shouldSkipPointerDragStart(e)) return
      focusWindow()

      const startX = e.clientX,
        startY = e.clientY
      const startPos = { ...pos }

      startPointerSession(e, {
        onMove: (ev) => {
          setPos({
            x: Math.max(
              0,
              Math.min(
                window.innerWidth - 100,
                startPos.x + ev.clientX - startX
              )
            ),
            y: Math.max(
              40,
              Math.min(
                window.innerHeight - 50,
                startPos.y + ev.clientY - startY
              )
            ),
          })
        },
      })
    },
    [focusWindow, pos]
  )

  // Resize
  const onResizeStart = useCallback(
    (e) => {
      e.stopPropagation()
      focusWindow()
      const startX = e.clientX,
        startY = e.clientY
      const startW = size.w,
        startH = size.h

      startPointerSession(e, {
        onMove: (ev) => {
          setSize({
            w: Math.max(360, startW + ev.clientX - startX),
            h: Math.max(250, startH + ev.clientY - startY),
          })
        },
      })
    },
    [focusWindow, size]
  )

  const handleCopy = () => {
    const errorLines = errorMeta
      ? [
          "",
          ...errorMeta.rows.map(([label, value]) => `${label}: ${value}`),
          errorMeta.rawDetail ? `Detail: ${errorMeta.rawDetail}` : "",
          errorMeta.rawResponse ? `Raw Response: ${errorMeta.rawResponse}` : "",
        ]
          .filter(Boolean)
          .join("\n")
      : ""
    navigator.clipboard
      ?.writeText(`${task.result || task.error || ""}${errorLines}`.trim())
      .catch(() => {})
  }

  const handleDiscuss = () => {
    openChat(task.agentId)
    addMessage(task.agentId, {
      role: "system",
      text: `📎 Referencing task: ${task.label}`,
      ts: Date.now(),
    })
    addMessage(task.agentId, {
      role: "user",
      text: `Let's discuss the results of "${task.label}".${summary ? ` Summary: ${summary}` : ""}\n\nHere's what you produced:\n\n${(task.result || "").slice(0, 300)}${(task.result || "").length > 300 ? "..." : ""}`,
      ts: Date.now(),
    })
  }

  return (
    <div
      {...focusProps}
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: size.w,
        height: size.h,
        background: "#0a0a0e",
        border: `1px solid ${color}30`,
        borderRadius: 10,
        zIndex,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
      }}
    >
      {/* Header — drag handle */}
      <div
        onPointerDown={onDragStart}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "8px 12px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          cursor: "grab",
          userSelect: "none",
          gap: 8,
          flexShrink: 0,
          touchAction: "none",
        }}
      >
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: color,
          }}
        />
        <span style={{ ...mono, fontSize: 11, color: "white", flex: 1 }}>
          {agent?.name} — {task.label}
        </span>
        <span
          style={{
            ...mono,
            fontSize: 8,
            color: statusColor,
          }}
        >
          {statusLabel} · {timeAgo(task.completedAt)}
        </span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.4)",
            fontSize: 14,
            cursor: "pointer",
            ...mono,
            padding: "0 2px",
          }}
        >
          ×
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px" }}>
        {summary && (
          <div
            style={{
              background: `${color}10`,
              border: `1px solid ${color}30`,
              borderRadius: 8,
              padding: "10px 12px",
              marginBottom: 10,
            }}
          >
            <div
              style={{
                ...mono,
                fontSize: 8,
                color: `${color}cc`,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 4,
              }}
            >
              Summary
            </div>
            <div
              style={{
                ...mono,
                fontSize: 12,
                color: "rgba(255,255,255,0.82)",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              {summary}
            </div>
          </div>
        )}

        {/* Steps */}
        {task.steps?.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <button
              onClick={() => setStepsOpen(!stepsOpen)}
              style={{
                background: "none",
                border: "none",
                ...mono,
                fontSize: 9,
                color: "rgba(255,255,255,0.35)",
                cursor: "pointer",
                padding: 0,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              {stepsOpen ? "▾" : "▸"} {task.steps.length} steps
            </button>
            {stepsOpen && (
              <div
                style={{
                  marginTop: 4,
                  paddingLeft: 8,
                  borderLeft: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                {task.steps.map((step, i) => (
                  <div
                    key={i}
                    style={{
                      ...mono,
                      fontSize: 8,
                      color: "rgba(255,255,255,0.3)",
                      padding: "2px 0",
                    }}
                  >
                    {step.toolEmoji || "⚡"} {step.toolLabel || step.tool}
                    {step.input && (
                      <span
                        style={{
                          color: "rgba(255,255,255,0.15)",
                          marginLeft: 4,
                        }}
                      >
                        {step.input.slice(0, 50)}
                      </span>
                    )}
                    {step.result && (
                      <div
                        style={{
                          color: "rgba(255,255,255,0.2)",
                          marginLeft: 12,
                        }}
                      >
                        → {step.result.slice(0, 60)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Media artifacts */}
        {artifacts.length > 0 && (
          <div
            style={{
              marginBottom: 10,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {artifacts.map((artifact, i) => (
              <TaskArtifactPreview
                key={artifact.storage_path || i}
                artifact={artifact}
              />
            ))}
          </div>
        )}

        {/* Result */}
        <div
          style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 8,
            padding: "12px 14px",
          }}
        >
          <div
            style={{
              fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
              fontSize: 13,
              color: "rgba(255,255,255,0.8)",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {resultText ||
              task.error ||
              (isCancelled ? "Task was cancelled." : "No output.")}
          </div>
        </div>

        {!isComplete && errorMeta && (
          <div
            style={{
              marginTop: 10,
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 8,
              padding: "12px 14px",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div
              style={{
                ...mono,
                fontSize: 8,
                color: `${color}cc`,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Error Details
            </div>
            {errorMeta.rows.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(90px, 110px) 1fr",
                  gap: "6px 10px",
                }}
              >
                {errorMeta.rows.map(([label, value]) => (
                  <Fragment key={`${label}:${value}`}>
                    <div
                      style={{
                        ...mono,
                        fontSize: 10,
                        color: "rgba(255,255,255,0.42)",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {label}
                    </div>
                    <div
                      style={{
                        ...mono,
                        fontSize: 11,
                        color: "rgba(255,255,255,0.82)",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {value}
                    </div>
                  </Fragment>
                ))}
              </div>
            )}
            {errorMeta.rawDetail && (
              <div>
                <div
                  style={{
                    ...mono,
                    fontSize: 9,
                    color: "rgba(255,255,255,0.42)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 4,
                  }}
                >
                  Detail
                </div>
                <div
                  style={{
                    ...mono,
                    fontSize: 11,
                    color: "rgba(255,255,255,0.82)",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  {errorMeta.rawDetail}
                </div>
              </div>
            )}
            {errorMeta.rawResponse && errorMeta.rawResponse !== errorMeta.rawDetail && (
              <div>
                <div
                  style={{
                    ...mono,
                    fontSize: 9,
                    color: "rgba(255,255,255,0.42)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 4,
                  }}
                >
                  Raw Response
                </div>
                <div
                  style={{
                    ...mono,
                    fontSize: 11,
                    color: "rgba(255,255,255,0.82)",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  {errorMeta.rawResponse}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div
        style={{
          padding: "8px 14px 10px",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          gap: 6,
          flexShrink: 0,
        }}
      >
        <button onClick={handleCopy} style={actionBtn(color)}>
          📋 Copy
        </button>
        <button onClick={handleDiscuss} style={actionBtn(color)}>
          💬 Discuss
        </button>
        <button
          onClick={onClose}
          style={{
            ...actionBtn(color),
            background: "none",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "rgba(255,255,255,0.4)",
          }}
        >
          Close
        </button>
      </div>

      {/* Resize handle */}
      <div
        onPointerDown={onResizeStart}
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: 14,
          height: 14,
          cursor: "nwse-resize",
          touchAction: "none",
        }}
      />
    </div>
  )
}

function actionBtn(color) {
  return {
    flex: 1,
    height: 28,
    ...{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" },
    fontSize: 9,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    background: `${color}12`,
    border: `1px solid ${color}44`,
    color: `${color}cc`,
    borderRadius: 5,
    cursor: "pointer",
  }
}

function TaskArtifactPreview({ artifact }) {
  const { signed_url: url, kind, mime, filename } = artifact
  // A storage_path-only artifact reaches us when signing hasn't completed yet
  // (e.g. taskExecutor failed to round-trip through post_world_task_event).
  // Render nothing rather than emit <img src={undefined}> which shows as a
  // broken-image icon.
  if (!url) return null
  const mediaStyle = {
    maxWidth: "100%",
    maxHeight: 320,
    borderRadius: 6,
    display: "block",
  }
  const frameStyle = {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 8,
    padding: 8,
  }

  const resolvedKind =
    kind || (typeof mime === "string" ? mime.split("/")[0] : "")

  if (resolvedKind === "image" || resolvedKind === "gif") {
    return (
      <div style={frameStyle}>
        <img src={url} alt={filename || "artifact"} style={mediaStyle} />
      </div>
    )
  }
  if (resolvedKind === "video") {
    return (
      <div style={frameStyle}>
        <video src={url} controls style={mediaStyle} />
      </div>
    )
  }
  if (resolvedKind === "audio") {
    return (
      <div style={frameStyle}>
        <audio src={url} controls style={{ width: "100%" }} />
      </div>
    )
  }

  return (
    <div style={frameStyle}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          ...mono,
          fontSize: 11,
          color: "rgba(180,210,255,0.9)",
          textDecoration: "none",
        }}
      >
        📎 {filename || "Download artifact"}
      </a>
    </div>
  )
}
